import { CardMovement, gameMutations } from '@/shared/state/gameMutations.ts'
import { MinionAction, MinionActionNames, MinionActionType } from '@/shared/types/state.ts'
import { Player } from '@/shared/model/Player.ts'
import { usePlayersStore } from '@/client/state/players.ts'
import { useBusStore, useGameBusStore } from '@/client/store/bus.ts'
import { useGameStateStore } from '@/client/store/gameState.ts'
import { Card, LibraryCard, Minion } from '@/shared/model/Card.ts'
import { CARD_HEIGHT, CARD_WIDTH, GRID_SIZE, PLAY_AREA_WIDTH } from '@/shared/const/game.ts'
import { selfSecureName } from '@/client/state/self.ts'
import { createActionCardAction, resolveCost } from '@/shared/state/minionActions.ts'
import { cardHalfExtents, findFreePlayPosition, getCardScale } from '@/client/game/utils.ts'
import { useUIFeatures } from '@/client/game/composables/useUIFeatures.ts'
import { LibraryCardType } from '@/shared/const/model.ts'
import { RegionCategory } from '@/client/game/types.ts'
import { rotatePoint } from '@/shared/state/freeTableLayout.ts'

/**
 * "Automatic cost payment" preference ( experimental ).
 *
 * When the local user has enabled it, playing one of their own cards also emits
 * the mutations that pay its cost, so they don't have to adjust pool/blood by
 * hand :
 * - a Master played from hand pays its pool cost ;
 * - an Action Modifier / Reaction / Combat card played on a minion pays its
 *   blood cost ( on the minion ) and its pool cost ( on the minion controller ).
 * Only the activated user's client emits these ; other players just receive the
 * mutations like any other.
 */
function applyAutomaticCost(card: Card, byMinion: Minion | undefined) {
    const players = usePlayersStore()
    const gameBus = useGameBusStore()
    const { automaticCostPaymentEnabled } = useUIFeatures()

    if (
        !automaticCostPaymentEnabled.value ||
        !(card instanceof LibraryCard) ||
        card.controller.oid != players.selfPlayerOid
    ) {
        return
    }

    // Master played from hand : pay its pool cost. Only show the indicator if the
    // pool was actually spent ( the mutation is rejected when pool is too low ).
    // A variable "X" cost has no declared value here, so it resolves to 0.
    if (card.type == LibraryCardType.Master) {
        const poolCost = resolveCost(card.poolCost)
        if (poolCost > 0) {
            const paid = gameMutations.changePool.actSelf({
                player: card.controller,
                amount: -poolCost,
            })
            if (paid.isValid) {
                gameBus.showCardCost(card.oid, 0, poolCost)
            }
        }
        return
    }

    // Action modifier / reaction / combat played on a minion : pay its blood cost
    // ( on the minion ) and its pool cost ( on the minion controller ). Only the
    // costs actually spent are shown : a mutation is rejected when the minion has
    // not enough blood or the player not enough pool, and showing an unpaid cost
    // would be incoherent.
    if (
        byMinion &&
        (card.type == LibraryCardType.ActionModifier ||
            card.type == LibraryCardType.Reaction ||
            card.type == LibraryCardType.Combat)
    ) {
        // A variable "X" cost has no declared value here, so it resolves to 0.
        const bloodCost = resolveCost(card.bloodCost)
        const poolCost = resolveCost(card.poolCost)
        const bloodPaid =
            bloodCost > 0 &&
            gameMutations.changeBlood.actSelf({ card: byMinion, amount: -bloodCost }).isValid
        const poolPaid =
            poolCost > 0 &&
            gameMutations.changePool.actSelf({
                player: byMinion.controller,
                amount: -poolCost,
            }).isValid
        gameBus.showCardCost(card.oid, bloodPaid ? bloodCost : 0, poolPaid ? poolCost : 0)
    }
}

export function playCard({
    card,
    byMinion,
    movement,
}: {
    card: Card
    byMinion?: Minion
    movement?: CardMovement
}) {
    const players = usePlayersStore()
    const gameBus = useGameBusStore()
    const gameState = useGameStateStore()
    const player = card.controller

    // Free Table : there's no per-player Ready region on the shared table.
    // Played cards land on gameState.table instead, near the player's widget.
    const toCardRegion = gameState.isFreeTable && gameState.table ? gameState.table : player.ready

    let x: number
    let y: number
    if (movement?.x !== undefined && movement.y !== undefined) {
        // Explicit drop position chosen by the user : respect it.
        x = movement.x
        y = movement.y
    } else {
        // Auto placement near the acting minion ( or a default spot ), nudged
        // by findFreePlayPosition() to avoid sitting on top of cards already
        // in play. The default spot is the center of the player's own mat in
        // standard mode, or their widget on the shared Free Table.
        const scale = getCardScale(RegionCategory.Table, toCardRegion)
        const cardHalfWidth = (CARD_WIDTH * scale) / 2
        const cardHalfHeight = (CARD_HEIGHT * scale) / 2

        let x0: number
        let y0: number
        if (byMinion) {
            // True rendered center of the acting minion ( see cardHalfExtents ).
            const { halfWidth: minionHalfWidth, halfHeight: minionHalfHeight } = cardHalfExtents(
                byMinion,
                scale,
            )
            const minionCenterX = byMinion.x + minionHalfWidth
            const minionCenterY = byMinion.y + minionHalfHeight

            // On Free Table, "directly above the minion" is relative to its
            // owner-facing rotation ( ownerFacingRotation ), not the world
            // y-axis - rotate the offset the same way before landing on it in
            // world coordinates. Standard mode has no such rotation.
            const rotation = byMinion.facingRotation()
            const worldOffset = rotatePoint(
                { x: 0, y: -(minionHalfHeight + cardHalfHeight) },
                rotation,
            )

            x0 = minionCenterX + worldOffset.x - cardHalfWidth
            y0 = minionCenterY + worldOffset.y - cardHalfHeight
        } else if (gameState.isFreeTable) {
            x0 = player.widgetPosition.x
            y0 = player.widgetPosition.y
        } else {
            x0 = PLAY_AREA_WIDTH / 2 - 4 * GRID_SIZE
            y0 = 8 * GRID_SIZE
        }

        ;({ x, y } = findFreePlayPosition(toCardRegion, card, x0, y0))
    }

    gameMutations.moveCardToRegion.act(player, {
        card,
        fromCardRegion: card.region,
        toCardRegion,
        x,
        y,
        byMinion,
    })

    applyAutomaticCost(card, byMinion)

    if (player.oid == players.selfPlayerOid) {
        gameBus.selectedCards = [card]
    }
}

export function resetDeclaration() {
    const gameBus = useGameBusStore()
    gameBus.declaringTargetOrigin = null
    gameBus.actionDeclaration = {
        type: null,
        actingMinion: null,
        usage: null,
        validTargets: null,
        validActionCards: null,
    }
}

export function declareAction(action: MinionAction, player?: Player) {
    const players = usePlayersStore()

    if (!player) {
        if (!players.selfPlayer) {
            return
        }
        player = players.selfPlayer
    }

    resetDeclaration()

    gameMutations.ACTION_declareAction.act(player, { minionAction: action })

    if (action.target) {
        gameMutations.UI_addTargetDeclaration.act(player, {
            origin:
                action.type == MinionActionType.ActionCardFromHand ?
                    action.card
                :   action.actingMinion,
            target: action.target,
        })
    }

    if (
        action.type == MinionActionType.ActionCardFromHand &&
        action.card.region == player.hand &&
        !player.isBot
    ) {
        playCard({ card: action.card, byMinion: action.actingMinion })
    }
}

export function declareActionCardFromHand(actingMinion: Minion, card: LibraryCard) {
    declareAction(createActionCardAction(actingMinion, card, {}))
}

export function startTargetDeclaration(card: Card) {
    const gameBus = useGameBusStore()
    gameBus.declaringTargetOrigin = card
}

export function validateTargetDeclaration(target: Card | Player) {
    const gameBus = useGameBusStore()
    const gameState = useGameStateStore()

    if (!gameBus.declaringTargetOrigin) {
        return
    }

    // Declaring the target(s) of the action card already in progress : several
    // targets can be declared, each adding its own arrow. Record the target in
    // the action usage and draw the arrow, skipping an exact duplicate.
    const action = gameState.action
    if (
        action &&
        action.minionAction.type == MinionActionType.ActionCardFromHand &&
        gameBus.declaringTargetOrigin.oid == action.minionAction.card.oid
    ) {
        const origin = action.minionAction.card
        const alreadyDeclared = gameState.targetDeclarations.some(
            declaration =>
                declaration.originOid == origin.oid && declaration.targetOid == target.oid,
        )
        if (!alreadyDeclared) {
            gameMutations.ACTION_updateUsage.actSelf({
                usage: { ...action.minionAction.usage, target },
            })
            gameMutations.UI_addTargetDeclaration.actSelf({ origin, target })
        }
        resetDeclaration()
        return
    }

    // If there was an action pending declaration, we can now declare it
    if (gameBus.actionDeclaration.type && gameBus.actionDeclaration.actingMinion) {
        // If this is not a valid target, abort
        if (
            gameBus.actionDeclaration.validTargets &&
            // @ts-expect-error if target is a player, includes will return false, which is fine
            !gameBus.actionDeclaration.validTargets.includes(target)
        ) {
            if (target instanceof Player) {
                resetDeclaration()
            } else {
                useBusStore().alertWarning(
                    `Invalid target for action ${MinionActionNames[gameBus.actionDeclaration.type]} : ${selfSecureName(target)}`,
                )
            }
            return
        }

        declareAction({
            type: gameBus.actionDeclaration.type,
            actingMinion: gameBus.actionDeclaration.actingMinion,
            target,
        } as MinionAction)
    }
    // Simple target declaration, no action
    else {
        gameMutations.UI_addTargetDeclaration.actSelf({
            origin: gameBus.declaringTargetOrigin,
            target,
        })
    }

    resetDeclaration()
}

export function validateActionCardDeclaration(card: Card) {
    const gameBus = useGameBusStore()

    if (
        !gameBus.actionDeclaration.type ||
        !gameBus.actionDeclaration.actingMinion ||
        !gameBus.actionDeclaration.validActionCards
    ) {
        return
    }

    if (
        gameBus.actionDeclaration.type != MinionActionType.ActionCardFromHand &&
        gameBus.actionDeclaration.type != MinionActionType.ActionInPlay
    ) {
        return
    }

    if (!gameBus.actionDeclaration.validActionCards.includes(card)) {
        useBusStore().alertWarning(`Invalid action card : ${card.name}`)
        return
    }

    declareAction({
        type: gameBus.actionDeclaration.type,
        actingMinion: gameBus.actionDeclaration.actingMinion,
        card,
        // usage declaration TODO
        usage: {},
    } as MinionAction)
    resetDeclaration()
}
